import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { loadingHttpContext } from 'src/app/common/interceptors/tokens';
import { SearchState } from 'src/app/interfaces/search.interface';
import {
  ConnectionRequestAction,
  EmptyResponse,
  PatientDistributionFilter,
  RelationGeography,
} from 'src/app/swagger-api';
import { DefaultService } from 'src/app/swagger-api/api/default.service';
import { assertNonNullish } from 'src/app/utils';
import { SearchCategories } from '../../search/search.enum';
import { mapPatientLink, mapRequestCount } from '../relation-request';
import {
  PatientLink,
  PatientLinkRequests,
  RelationRequests,
  RelationRequestsCount,
  SuggestedUser,
} from '../relation-request.interface';
import { UserRelationRequestService } from './relation-request.service';

@Injectable()
export class PatientLinkRequestService extends UserRelationRequestService<PatientLink> {
  private apiService = inject(DefaultService);

  protected fetchTotalCountRequests(): Observable<RelationRequestsCount> {
    return this.apiService.restMedicalPatientLinksCounters().pipe(map(mapRequestCount));
  }

  protected updateSearchState(state: SearchState): SearchState {
    const { params } = state;

    return {
      ...state,
      params: { queryText: params?.queryText ?? '', itemKind: SearchCategories.PATIENTS },
    };
  }

  protected fetchAcceptedRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
  ): Observable<RelationRequests<PatientLink>> {
    return this.apiService.restMedicalPatientLinksIndexLinked(offset, limit, sorting).pipe(
      map((requests: PatientLinkRequests) => ({
        items: requests.items.map(mapPatientLink),
        totalCount: requests.total_count,
      })),
    );
  }

  protected fetchReceivedRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
  ): Observable<RelationRequests<PatientLink>> {
    return this.apiService.restMedicalPatientLinksIndexReceived(offset, limit, sorting).pipe(
      map((requests: PatientLinkRequests) => ({
        items: requests.items.map(mapPatientLink),
        totalCount: requests.total_count,
      })),
    );
  }

  protected fetchSentRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
  ): Observable<RelationRequests<PatientLink>> {
    return this.apiService.restMedicalPatientLinksIndexSent(offset, limit, sorting).pipe(
      map((requests: PatientLinkRequests) => ({
        items: requests.items.map(mapPatientLink),
        totalCount: requests.total_count,
      })),
    );
  }

  protected fetchRegularUserRelation(_: string): Observable<RelationRequests<PatientLink>> {
    throw new Error('Method not implemented.');
  }

  protected fetchRelationGeography(
    userId: string,
    filter_by?: PatientDistributionFilter,
    filter_value?: string,
  ): Observable<RelationGeography> {
    return this.apiService.restMedicalPatientsGeographyGet(userId, filter_by, filter_value, loadingHttpContext());
  }

  protected accept({ entityId }: PatientLink): Observable<EmptyResponse> {
    return this.apiService.restMedicalPatientLinkPut(
      { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Verified },
      entityId,
    ) as Observable<EmptyResponse>;
  }

  protected reject({ entityId }: PatientLink): Observable<EmptyResponse> {
    return this.apiService.restMedicalPatientLinkPut(
      { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Rejected },
      entityId,
    ) as Observable<EmptyResponse>;
  }

  protected remove({ entityId }: PatientLink): Observable<EmptyResponse> {
    return this.apiService.restMedicalPatientLinkDelete(entityId) as Observable<EmptyResponse>;
  }

  protected create({ user }: SuggestedUser): Observable<EmptyResponse> {
    return this.apiService.restMedicalPatientLinksPost({ patientUserId: user.user_id }) as Observable<EmptyResponse>;
  }

  protected removeByUser(user: SuggestedUser): Observable<EmptyResponse> {
    assertNonNullish(user.patientLinkStatus?.entity_id);
    return this.apiService.restMedicalPatientLinkDelete(user.patientLinkStatus.entity_id) as Observable<EmptyResponse>;
  }

  protected override acceptByUser(user: SuggestedUser): Observable<EmptyResponse> {
    assertNonNullish(user.patientLinkStatus?.entity_id);
    return this.apiService.restMedicalPatientLinkPut(
      { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Verified },
      user.patientLinkStatus.entity_id,
    ) as Observable<EmptyResponse>;
  }
}
